import { Offcanvas } from 'react-bootstrap';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useToast } from 'context/ToastContext';
import WarehouseNameForm from './WarehouseNameForm';
import {
    getWarehouseNameConfig,
    submitWarehouseName,
    type WarehouseNameKind,
    type WarehouseNameMode,
} from './model';

interface WarehouseNameOffcanvasProps {
    kind: WarehouseNameKind;
    mode: WarehouseNameMode;
    show: boolean;
    setShow: (show: boolean) => void;
    entityId?: number | null;
    initialName?: string;
    onSuccess: () => Promise<void>;
}

const WarehouseNameOffcanvas = ({
    kind,
    mode,
    show,
    setShow,
    entityId = null,
    initialName = '',
    onSuccess,
}: WarehouseNameOffcanvasProps) => {
    const { t } = useTranslation(['Warehouses', 'common']);
    const { showToast } = useToast();
    const config = getWarehouseNameConfig(kind, mode);
    const inputId = `warehouse-name-${kind}-${mode}`;

    const handleClose = () => setShow(false);

    return (
        <Offcanvas show={show} onHide={handleClose} placement="end">
            <Offcanvas.Header className="border-bottom" closeButton>
                <Offcanvas.Title>{t(config.titleKey)}</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body className="position-relative pb-0 d-flex flex-column px-0">
                <Formik
                    enableReinitialize={mode === 'rename'}
                    initialValues={{ name: mode === 'rename' ? initialName || '' : '' }}
                    onSubmit={(values, helpers) =>
                        submitWarehouseName(values, helpers, {
                            mode,
                            entityId,
                            config,
                            t,
                            showToast,
                            setShow,
                            onSuccess,
                        })
                    }
                >
                    {({ handleSubmit, isSubmitting, errors, touched }) => (
                        <WarehouseNameForm
                            mode={mode}
                            label={t(config.labelKey)}
                            inputId={inputId}
                            handleSubmit={handleSubmit}
                            isSubmitting={isSubmitting}
                            error={touched.name && errors.name ? String(errors.name) : ''}
                            autofocus={show}
                        />
                    )}
                </Formik>
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default WarehouseNameOffcanvas;
export type { WarehouseNameKind, WarehouseNameMode };
